import { createContext, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import { cva } from "class-variance-authority";
import { FlexCol, FlexRow } from "src/utils/flexLayouts";
import { twMerge } from "tailwind-merge";

import moment from "moment";
import { useSearchParams } from "react-router-dom";
import { myTaskActionsListName } from "src/api/config/myTasksActions.config";
import { EmptyList } from "src/components/EmptyList";
import Loading from "src/components/Loading";
import { NotificationBigIcon } from "src/components/ui-icons/sidebar/NotificationIcon";
import SortIcon from "src/components/ui-icons/table/SortIcon";
import { TableHeaderColumnProps } from "src/components/ui/table/Table";
import { UserAvatarItem } from "src/components/ui/TGUserAvatarsList";
import { useAppSelector } from "src/hooks/redux";
import { useGetSelect } from "src/hooks/redux-selectors";
import { selectGetStatus } from "src/modules/spItems/selectors";
import { TableColumnWithIcon } from "src/pages/DashboardPage/acadia/mock";
import { ActionStatus } from "src/pages/DashboardPage/mytasks/meetingsPage/components/ActionStatus";
import { LookUpRecord, UserRecord } from "src/types/main";
import { shortNameFromEmail } from "src/utils/users.utils";
import { isStrapi } from "src/api/config/systemSwitch";
import { siteUrl } from "src/api/config";

type TableHeadType = "CreateDate" | "Description" | "DueDate" | "Status" | "Responsible";

export type TableBodyItem = {
  ID: number;
  CreateDate: string;
  Description: string;
  DueDate: string;
  Status: LookUpRecord & { color: string };
  Responsible: UserRecord;
};

const tableHeaderContent: Record<TableHeadType, TableColumnWithIcon> = {
  CreateDate: {
    title: "Дата создания",
    sort: true,
    style: { width: "15%" },
  },
  Description: {
    title: "Описание",
    sort: true,
    style: { width: "33%" },
  },
  DueDate: {
    title: "Срок выполнения",
    sort: true,
    style: { width: "15%" },
  },
  Status: {
    title: "Статус",
    sort: false,
    style: { width: "10%" },
  },
  Responsible: {
    title: "Ответственный",
    sort: true,
    style: { width: "27%" },
  },
};

function DefaultActionInMeetingTable() {
  const { data: getActions } = useGetSelect(myTaskActionsListName);

  const [items, setItems] = useState<TableBodyItem[]>([]);
  const [sortedField, setSortedField] = useState<keyof TableBodyItem | null>("CreateDate");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc" | "default">("desc");

  const handleSort = (field: keyof TableBodyItem) => {
    if (sortedField === field) {
      setSortDirection((prevDirection) =>
        prevDirection === "asc" ? "desc" : prevDirection === "desc" ? "default" : "asc"
      );
    } else {
      setSortedField(field);
      setSortDirection("asc");
    }
  };

  const sortedTableBodyContent = useMemo(() => {
    if (!sortedField || sortDirection === "default") {
      return items;
    }

    const sortedData = [...items];

    sortedData.sort((a, b) => {
      if (sortedField === "CreateDate" || sortedField === "DueDate") {
        const dateA = moment(a[sortedField], "DD.MM.YYYY");
        const dateB = moment(b[sortedField], "DD.MM.YYYY");

        if (!dateA.isValid() || !dateB.isValid()) {
          console.error("Неверная дата", a[sortedField], b[sortedField]);
          return 0;
        }

        return sortDirection === "asc"
          ? dateA.diff(dateB) // если dateA после dateB вернется положительное число
          : dateB.diff(dateA); // для обратного порядка сортировки
      }

      if (sortedField === "Responsible") {
        return sortDirection === "asc"
          ? a[sortedField].Name.localeCompare(b[sortedField].Name)
          : b[sortedField].Name.localeCompare(a[sortedField].Name);
      }

      const valueA = a[sortedField];
      const valueB = b[sortedField];

      if (typeof valueA === "string" && typeof valueB === "string") {
        return sortDirection === "asc" ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
      } else if (typeof valueA === "boolean" && typeof valueB === "boolean") {
        return sortDirection === "asc" ? Number(valueA) - Number(valueB) : Number(valueB) - Number(valueA);
      }

      return 0;
    });

    return sortedData;
  }, [sortedField, sortDirection, items]) as TableBodyItem[];

  useLayoutEffect(() => {
    if (getActions.length > 0) {
      const itemsData: TableBodyItem[] = getActions.map((action) => ({
        ID: action.ID,
        CreateDate: action.Created.format("DD.MM.YYYY"),
        Description: action.Description,
        DueDate: action.DateForDone.format("DD.MM.YYYY"),
        Responsible: action.Responsible,
        Status: { ...action.Status, color: isStrapi ? action.Status.Metadata.Color : action.Status_x003a_Color.Title },
      }));

      setItems(itemsData);
    } else {
      setItems([]);
    }
  }, [getActions]);

  return (
    <Table
      tableName="Table with meeting actions"
      header={tableHeaderContent}
      body={sortedTableBodyContent}
      handleSort={handleSort}
      sortDirection={sortDirection}
      sortedField={sortedField}
    />
  );
}

export default DefaultActionInMeetingTable;

function Table({
  header,
  body,
  tableName,
  handleSort,
  sortedField,
  sortDirection,
}: {
  header: typeof tableHeaderContent;
  body: TableBodyItem[];
  tableName: string;
  handleSort: (f: keyof TableBodyItem) => void;
  sortedField: keyof TableBodyItem | null;
  sortDirection: "asc" | "desc" | "default";
}) {
  return (
    <div id="table-scroll" className="px-6 pt-0 w-auto bg-white">
      <FlexRow className="bg-white rounded-lg table-custom shadow-custom">
        <FlexCol className="w-full">
          <TableList
            head={header}
            body={body}
            sort={handleSort}
            sortDirection={sortDirection}
            sortedField={sortedField}
            tableName={tableName}
          />
        </FlexCol>
      </FlexRow>
    </div>
  );
}

function TableList({
  head,
  body,
  sort,
  sortDirection,
  sortedField,
}: {
  head: Omit<typeof tableHeaderContent, "ID">;
  body: TableBodyItem[];
  sort: (f: keyof TableBodyItem) => void;
  sortedField: keyof TableBodyItem | null;
  sortDirection: "asc" | "desc" | "default";
  tableName: string;
}) {
  const [query, setQuery] = useSearchParams();
  const scrollRef = useRef<HTMLDivElement>(null);

  const myTaskActionsStatus = useAppSelector((state) => selectGetStatus(state, myTaskActionsListName));

  // Скроллим к последнему открытому действию после обновления данных
  useEffect(() => {
    const actionCardId = query.get("actionCard");

    // Если модалка закрыта и есть сохраненный ID действия
    if (!actionCardId && body.length > 0) {
      const lastActionId = sessionStorage.getItem("lastViewedActionId");

      if (lastActionId) {
        requestAnimationFrame(() => {
          const element = document.querySelector(`[data-action-id="${lastActionId}"]`);
          if (element) {
            element.scrollIntoView({ behavior: "auto", block: "center" });
          }
        });
      }
    }
  }, [body, query]);

  function handleClickActionCard(item: TableBodyItem) {
    // Сохраняем ID действия для возврата к нему после закрытия модалки
    sessionStorage.setItem("lastViewedActionId", item.ID.toString());

    // const newSearchParams = new URLSearchParams(query);
    // newSearchParams.set("actionCard", item.ID.toString());
    // setQuery(newSearchParams);
    window.open(`${siteUrl}/dashboard/3?actionCard=${item.ID.toString()}`, "_blank");
  }

  return (
    <div
      ref={scrollRef}
      className="flex flex-row px-6 pb-5 overflow-x-auto bg-white rounded-lg table-custom shadow-custom scrollbar scrollbar-thumb-rounded-lg scrollbar-h-2 scrollbar-thumb-[#D1D5DB] max-h-[calc(100vh-325px)] max-h-[calc(100dvh-325px)]"
    >
      <FlexCol className="divide-y min-w-[835px] grow relative min-h-fit">
        <FlexRow className="z-10 gap-x-4 py-5 text-black bg-white border-b sticky-custom">
          {Object.keys(head).map((fieldKey) => {
            const key = fieldKey as TableHeadType;

            return (
              <TableHeaderColumn
                key={fieldKey}
                tableHeaderContent={head[key]}
                sortDirection={sortedField === key ? sortDirection : "default"}
                onClick={() => sort(key)}
              />
            );
          })}
        </FlexRow>
        {myTaskActionsStatus !== "SUCCESS" ? (
          <div className="pt-4">
            <Loading />
          </div>
        ) : Array.isArray(body) && body.length > 0 ? (
          body.map((item) => <ListItem key={item.ID} item={item} openItem={handleClickActionCard} />)
        ) : (
          <EmptyList icon={<NotificationBigIcon />} title="У вас нет действий" />
        )}
      </FlexCol>
    </div>
  );
}

function ListItem({ item, openItem }: { item: TableBodyItem; openItem: (item: TableBodyItem) => void }) {
  return (
    <FlexRow
      data-action-id={item.ID}
      className="py-4 hover:bg-[#F3F4F6] hover:bg-opacity-50 hover:rounded-lg cursor-pointer gap-x-4 -mx-2 px-2"
      key={item.ID}
      onClick={() => openItem(item)}
    >
      <FlexCol className="break-words text-[#637381] justify-center" style={{ width: "15%" }}>
        {item.CreateDate}
      </FlexCol>
      <FlexCol className="break-words text-darkblue" style={{ width: "33%" }}>
        {item.Description}
      </FlexCol>
      <FlexCol className="break-words text-[#637381]" style={{ width: "15%" }}>
        {item.DueDate}
      </FlexCol>
      <FlexCol className="break-words text-[#637381]" style={{ width: "10%" }}>
        <ActionStatus title={item.Status.Title} color={item.Status.color} />
      </FlexCol>
      <FlexCol className={twMerge("break-words")} style={{ width: "27%" }}>
        <UserAvatarItem
          item={{
            name: item.Responsible.Name,
            shortName: shortNameFromEmail(item.Responsible.Email),
            email: item.Responsible.Email,
            id: item.Responsible.ID,
          }}
          single={true}
        />
      </FlexCol>
    </FlexRow>
  );
}

interface TemplateContextType {
  percentOneQuestion: number;
  totalPercentage: number;
  totalAnswersObject: Record<any, any>;
  totalQuestions: number;
  changeTotalPercentage: (v: number) => void;
  changeTotalQuestions: (v: number) => void;
  changeTotalAnswersObject: (key: string, v: any) => void;
  resetTotalPercentage: () => void;
  read: boolean;
}

const TemplateContext = createContext<TemplateContextType | null>(null);

export const useTemplateContext = (): TemplateContextType => {
  const context = useContext(TemplateContext);

  if (!context) {
    throw new Error("useTemplateContext должен использоваться внутри TemplateProvider");
  }

  return context;
};

export function TableHeaderColumn({ tableHeaderContent, sortDirection, onClick }: TableHeaderColumnProps) {
  return (
    <FlexCol className="select-none" style={tableHeaderContent.style}>
      <FlexRow className="gap-x-2 items-center">
        {tableHeaderContent.icon && <FlexCol>{tableHeaderContent.icon}</FlexCol>}
        <FlexCol className="font-medium whitespace-pre-line w-fit">{tableHeaderContent.title}</FlexCol>
        {tableHeaderContent.sort && (
          <FlexCol className="w-auto cursor-pointer" onClick={onClick}>
            <SortIcon sortDirection={sortDirection} />
          </FlexCol>
        )}
      </FlexRow>
    </FlexCol>
  );
}

const criticalItemStyle = cva("block h-2 w-2 rounded-full", {
  variants: {
    active: {
      true: "bg-[#BA0C2F]",
      false: "bg-[#8899A8]",
    },
  },
  defaultVariants: {
    active: false,
  },
});

export function CriticallyListItem({ item }: { item: boolean }) {
  return (
    <FlexRow className="items-center space-x-1">
      <FlexCol>
        <span className={twMerge(criticalItemStyle({ active: item }))}></span>
      </FlexCol>
      <FlexCol>{item ? "" : "Не "}Критичный</FlexCol>
    </FlexRow>
  );
}
